import { downloadExternalRequestFile } from "@synthesis/shared-services";
import { get2QueryKey, useDeleteProof, useGet2, useNotifyPaid } from "@synthesis/shared-services/external/payment/hooks";
import { IconCloseCircleFill, IconFileAdd } from "@synthesis/shared-icons";
import { Button, setPromiseSnack, setSnack } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { usePageStore } from "../../application-detail-page.lib";
import { invalidateExternalApplicationQueries } from "../../../../shared/utils/invalidate-application-queries";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { PaymentProofFileChip } from "./payment-proof-file-chip";

const PAYMENT_PROOF_DELETE_SNACK_MESSAGES = {
  pending: "Удаление файла...",
  fulfilled: "Подтверждение оплаты удалено",
  rejected: "Не удалось удалить файл. Попробуйте еще раз",
} as const;

const PAYMENT_NOTIFY_SNACK_MESSAGES = {
  pending: "Отправка уведомления...",
  fulfilled: "Уведомление об оплате отправлено",
  rejected: "Не удалось отправить уведомление. Попробуйте еще раз",
} as const;

type PaymentExternalContentProps = {
  requestId: string;
  className?: string;
};

export const PaymentExternalContent = ({ requestId, className }: PaymentExternalContentProps) => {
  const queryClient = useQueryClient();
  const downloadFile = useDownload();
  const { data: paymentView, isLoading } = useGet2({ path: { id: requestId } });
  const pageStore = usePageStore();
  const deleteProofMutation = useDeleteProof();
  const notifyPaidMutation = useNotifyPaid({
    mutation: {
      onSuccess: (updatedView) => {
        queryClient.setQueryData(get2QueryKey({ path: { id: requestId } }), updatedView);
        invalidateExternalApplicationQueries(queryClient, requestId);
      },
    },
  });

  const handleDeleteProof = async () => {
    const promise = deleteProofMutation.mutateAsync({ path: { id: requestId } }).then((updatedView) => {
      queryClient.setQueryData(get2QueryKey({ path: { id: requestId } }), updatedView);
    });

    setPromiseSnack({ promise, promiseSettings: { title: PAYMENT_PROOF_DELETE_SNACK_MESSAGES } });
  };

  const handleDownloadProof = async (proofFileId: string, proofFileName: string) => {
    try {
      const blob = await downloadExternalRequestFile(proofFileId);
      downloadFile({ id: proofFileId, baseName: proofFileName }, blob);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setSnack({
        title: "Не удалось скачать файл",
        description: message,
        icon: <IconCloseCircleFill className="size-4" />,
      });
    }
  };

  const handleNotifyPaid = () => {
    setPromiseSnack({
      promise: notifyPaidMutation.mutateAsync({ path: { id: requestId } }),
      promiseSettings: { title: PAYMENT_NOTIFY_SNACK_MESSAGES },
    });
  };

  if (isLoading) {
    return <DetailTabPlaceholder className={className} title="Загрузка данных об оплате" description="Пожалуйста, подождите..." />;
  }

  if (!paymentView) {
    return <DetailTabPlaceholder className={className} title="Данные об оплате недоступны" description="Не удалось загрузить информацию об оплате" />;
  }

  if (paymentView.invoiceReceived !== true) {
    return (
      <DetailTabPlaceholder
        className={className}
        title="Данные об оплате"
        description="Информация о счёте и подтверждении оплаты появится в этом разделе после выставления счёта"
      />
    );
  }

  const canUploadProof = paymentView.canUploadProof === true;
  const canNotify = paymentView.canNotify === true;
  const proofFileName = paymentView.proofUploaded === true ? (paymentView.proofFileName ?? null) : null;
  const proofFileId = paymentView.proofUploaded === true ? (paymentView.proofFileId ?? null) : null;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-2">
        <p className="paragraph-base text-type-minor">Счёт на оплату</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <span className="subtitle-sm text-type-light">Стоимость по счёту, ₽</span>
            <span className="paragraph-base text-type-common">
              {paymentView.invoiceAmount != null ? paymentView.invoiceAmount.toLocaleString("ru-RU") : "—"}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="subtitle-sm text-type-light">Номер счёта</span>
            <span className="paragraph-base text-type-common">{paymentView.invoiceNumber ?? "—"}</span>
          </div>
          <div className="flex flex-col">
            <span className="subtitle-sm text-type-light">Дата отправки счёта</span>
            <span className="paragraph-base text-type-common">
              {paymentView.invoiceSentAt ? dayjs(paymentView.invoiceSentAt).format("DD.MM.YYYY HH:mm") : "—"}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="subtitle-sm text-type-light">Подтверждение оплаты</span>
            {proofFileName ? (
              <PaymentProofFileChip
                fileName={proofFileName}
                disabled={deleteProofMutation.isPending}
                onDownload={proofFileId ? () => handleDownloadProof(proofFileId, proofFileName) : undefined}
                onRemove={canUploadProof ? handleDeleteProof : undefined}
              />
            ) : (
              <span className="paragraph-base text-type-common">—</span>
            )}
          </div>
        </div>
      </div>

      {canUploadProof || canNotify ? (
        <div className="flex gap-4">
          {canUploadProof ? (
            <Button
              theme="secondary"
              size="md"
              onClick={() => pageStore?.setPopupAction({ type: "payment-proof", requestId, isReplacement: paymentView.proofUploaded === true })}
              leftIcon={<IconFileAdd className="size-6" />}
            >
              Прикрепить подтверждение
            </Button>
          ) : null}
          {canNotify ? (
            <Button theme="primary" size="md" disabled={notifyPaidMutation.isPending} onClick={() => void handleNotifyPaid()}>
              Уведомить об оплате
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
